import React, { useLayoutEffect, useEffect, useState, useMemo, useRef } from "react";
import { Button, Col, Form, Row, Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import { getAllData } from "../actions/billActions";
import { adminGetAllUsers } from "../actions/userActions";
import axios from "axios";
import { CSVLink } from "react-csv";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import dayjs from "dayjs";
import useIsMobile from "../utils/useIsMobile";
import NoKeyboardInput from "../components/NoKeyboardInput";
import FormField from "../components/FormField";

const format_rupees = val =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(val);

const format_date = val => {
  var customParseFormat = require('dayjs/plugin/customParseFormat');
  dayjs.extend(customParseFormat);
  let format = ["DD/MM/YYYY hh:mm:ss a", "D/M/YYYY H:mm:ss a", "YYYY-MM-DDTHH:mm:ss.SSSZ"];
  return dayjs(val, format).format("DD-MM-YYYY hh:mm a");
};

const FILTER_KEY = 'reports_filters';

const loadFilters = () => {
  try {
    const saved = sessionStorage.getItem(FILTER_KEY);
    if (saved) {
      const f = JSON.parse(saved);
      return {
        startDate: f.startDate ? new Date(f.startDate) : new Date(),
        endDate:   f.endDate   ? new Date(f.endDate)   : new Date(),
        patientName: f.patientName || "",
        selectedUser: f.selectedUser || "",
      };
    }
  } catch { /* ignore */ }
  return null;
};

const Reports = () => {
  const userInfo = useSelector(state => state.userLogin.userInfo);
  const savedFilters = loadFilters();
  const [startDate, setStartDate] = useState(savedFilters?.startDate ?? new Date());
  const [endDate, setEndDate] = useState(savedFilters?.endDate ?? new Date());
  const [patientName, setPatientNme] = useState(savedFilters?.patientName ?? "");
  const dispatch = useDispatch();
  const invoices = useSelector(state => state.billInfo.data);
  const loading = useSelector(state => state.billInfo.loading);
  const [total, setTotal] = useState({ netAmount: 0, received: 0, balance: 0 });
  const { SearchBar } = Search;
  const isMobile = useIsMobile();
  const [downloadingId, setDownloadingId] = useState(null);
  const isAdmin = userInfo && userInfo.role === "admin";
  const { users: adminUsers } = useSelector(state => state.adminUsers || { users: [] });
  const [selectedUser, setSelectedUser] = useState(savedFilters?.selectedUser ?? "");
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // Persist filters to sessionStorage whenever they change
  useEffect(() => {
    sessionStorage.setItem(FILTER_KEY, JSON.stringify({ startDate, endDate, patientName, selectedUser }));
  }, [startDate, endDate, patientName, selectedUser]);

  const datePickerRef = useRef(null);

  const onDateChange = dates => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    if (end) {
      datePickerRef.current?.setOpen(false);
    }
  };

  useLayoutEffect(() => {
    handleSearch();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isAdmin) dispatch(adminGetAllUsers());
  }, [isAdmin, dispatch]);

  const handleSearch = () => {
    const filters = {
      start_date: startDate,
      end_date: endDate,
      patient_name: patientName
    };
    if (isAdmin && selectedUser) filters.userId = selectedUser;
    dispatch(getAllData(filters));
  };

  const editInvoice = invoiceNo => {
    window.location = `/billing?trfNo=${invoiceNo}`;
  };

  const viewInvoice = async invoiceNo => {
    setDownloadingId(invoiceNo);
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userInfo.token}`,
      },
    };
    try {
      const res = await axios.get(`/api/v1/bill/generatePdf/${invoiceNo}`, config);
      if (res.data.pdf) {
        const inLink = document.createElement("a");
        inLink.download = invoiceNo + ".pdf";
        inLink.href = "data:application/octet-stream;base64," + res.data.pdf;
        document.body.appendChild(inLink);
        inLink.click();
        document.body.removeChild(inLink);
      }
    } catch { } finally {
      setDownloadingId(null);
    }
  };

  // Memoize CSV data and totals
  const csvData = useMemo(() => {
    if (!loading && invoices && invoices.length) {
      let totalNet = 0, totalRec = 0, totalBal = 0;
      const rows = invoices.map(o1 => {
        // Only parse if items is a string
        let itemsArr = Array.isArray(o1.items)
          ? o1.items
          : typeof o1.items === "string"
            ? (() => { try { return JSON.parse(o1.items); } catch { return []; } })()
            : [];
        let testItems = itemsArr.map(o2 => o2.description).toString();
        o1.balance = Number(o1.net_amount - o1.advance);
        totalNet += o1.net_amount;
        totalRec += o1.advance;
        totalBal += o1.balance;
        return [
          o1.invoice_no,
          format_date(o1.invoice_date),
          o1.patient_name,
          o1.patient_age,
          o1.patient_gender,
          testItems,
          o1.patient_mobile,
          o1.reference_doctor,
          o1.net_amount,
          o1.balance,
          o1.payment_type
        ];
      });
      setTotal({ netAmount: totalNet, received: totalRec, balance: totalBal });
      return [
        ["TRF No.", "TRF Date", "Patient Name", "Age", "Gender", "Test Name with Test Code", "Mobile Number", "Reference Doctor", "Total Amount", "Balance Amount", "Payment Type"],
        ...rows
      ];
    }
    return [["TRF No.", "TRF Date", "Patient Name", "Age", "Gender", "Test Name with Test Code", "Mobile Number", "Reference Doctor", "Total Amount", "Balance Amount", "Payment Type"]];
  }, [loading, invoices]);

  const columns = [
    ...(isAdmin ? [{
      dataField: "user_name",
      text: "User / Lab",
      sort: true,
      formatter: (cellContent, row) => (
        <div>
          <div className="font-weight-bold">{row.user_name || 'Admin'}</div>
          <div className="small text-muted">{row.com_name || 'N/A'}</div>
        </div>
      )
    }] : []),
    {
      dataField: "invoice_date",
      text: "TRF Date",
      sort: true,
      formatter: (cell) => {
        const dateStr = format_date(cell);
        const [datePart, ...timeParts] = dateStr.split(" ");
        return (
          <div style={{ whiteSpace: 'nowrap' }}>
            <div>{datePart}</div>
            <div className="text-muted small">{timeParts.join(" ")}</div>
          </div>
        );
      }
    },
    {
      dataField: "invoice_no",
      text: "TRF No.",
      sort: true,
      headerStyle: { width: '100px' }
    },
    {
      dataField: "patient_name",
      text: "Patient Name",
      sort: true
    },
    {
      dataField: "patient_gender",
      text: "Gender",
      sort: true
    },
    {
      dataField: "patient_mobile",
      text: "Mobile No.",
      sort: true
    },
    {
      dataField: "payment_type",
      text: "Payment Type",
      sort: true
    },
    {
      dataField: "net_amount",
      text: "Net Amount",
      sort: true,
      formatter: cell => format_rupees(cell)
    },
    {
      dataField: "advance",
      text: "Balance",
      formatter: (cellContent, row) => format_rupees(row.net_amount - row.advance),
      sort: true,
    }
  ];

  const mobColumns = [
    {
      dataField: "invoice_date",
      text: "TRF Date",
      sort: true,
      formatter: (cell) => {
        const dateStr = format_date(cell);
        const [datePart, ...timeParts] = dateStr.split(" ");
        return (
          <div style={{ whiteSpace: 'nowrap' }}>
            <div>{datePart}</div>
            <div className="text-muted small">{timeParts.join(" ")}</div>
          </div>
        );
      },
      headerStyle: { width: '100px' },
      style: { textAlign: 'left' }
    },
    {
      dataField: "patient_name",
      text: "Patient Name",
      headerStyle: { width: '180px' },
      sort: true
    },
    {
      dataField: "net_amount",
      text: "Amount",
      sort: true,
      formatter: cell => format_rupees(cell),
      headerAlign: 'right',
      style: { textAlign: 'right' }
    }
  ];

  const columnsToUse = isMobile ? mobColumns : columns;

  return (
    <div className="reports-screen pb-5">
      <Modal show={showActionModal} onHide={() => setShowActionModal(false)} centered size="sm" contentClassName={isMobile ? "border-0 shadow-lg" : ""} style={isMobile ? { borderRadius: '15px' } : {}}>
        <Modal.Header closeButton className={isMobile ? "border-0 pb-0" : "pb-0"}>
          <Modal.Title className="h6 font-weight-bold">TRF Actions</Modal.Title>
        </Modal.Header>
        <Modal.Body className={isMobile ? "p-4 text-center" : "p-3 text-center"}>
          <div className="d-flex justify-content-center" style={{ gap: isMobile ? '15px' : '10px' }}>
            <Button
              variant="warning"
              className={isMobile ? "px-4 py-2 font-weight-bold shadow-sm flex-fill" : ""}
              style={isMobile ? { borderRadius: '8px' } : { borderRadius: '8px', lineHeight: '0.5rem' }}
              onClick={() => { setShowActionModal(false); editInvoice(selectedRow?.invoice_no); }}
            >
              <i className={`fa fa-pencil-square ${isMobile ? 'fa-lg' : ''} mr-2`}></i> Edit
            </Button>
            <Button
              variant="info"
              className={isMobile ? "px-4 py-2 font-weight-bold shadow-sm flex-fill" : ""}
              style={isMobile ? { borderRadius: '8px' } : { borderRadius: '8px', lineHeight: '0.5rem' }}
              disabled={!!downloadingId}
              onClick={() => { setShowActionModal(false); viewInvoice(selectedRow?.invoice_no); }}
            >
              <i className={`fa fa-download ${isMobile ? 'fa-lg' : ''} mr-2`}></i> {downloadingId === selectedRow?.invoice_no ? "Downloading..." : "Download"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <h3 className="font-weight-bold grey-900 mb-1" style={{ letterSpacing: '-0.8px', fontSize: 'clamp(1rem, 4vw, 1.4rem)' }}>Financial Reports</h3>
          <p className="text-muted mb-0">Overview and export data</p>
        </div>
        {invoices && invoices.length > 0 && (
          <CSVLink
            data={csvData}
            filename={"TRF_reports_" + dayjs().format("YYYYMMDDHHMM") + ".csv"}
            className="btn-action-premium btn-generate-trf py-2 px-4 no-decoration"
            style={{ minWidth: '100px' }}
          >
            <i className="fa fa-file-excel-o mr-2"></i> Export
          </CSVLink>
        )}
      </div>

      {/* Metrics Row */}
      <Row className="mb-4">
        <Col md={4}>
          <div className="metric-card-compact border-accent-indigo custom-card-boxshadow p-3">
            <p className="text-muted small text-uppercase font-weight-bold mb-1" style={{ letterSpacing: '0.5px' }}>Total Revenue</p>
            <h4 className="mb-0 font-weight-bold text-dark">{format_rupees(total.netAmount)}</h4>
          </div>
        </Col>
        <Col md={4}>
          <div className="metric-card-compact border-accent-emerald custom-card-boxshadow p-3">
            <p className="text-muted small text-uppercase font-weight-bold mb-1" style={{ letterSpacing: '0.5px' }}>Payments Received</p>
            <h4 className="mb-0 font-weight-bold text-success">{format_rupees(total.received)}</h4>
          </div>
        </Col>
        <Col md={4}>
          <div className="metric-card-compact border-accent-rose custom-card-boxshadow p-3">
            <p className="text-muted small text-uppercase font-weight-bold mb-1" style={{ letterSpacing: '0.5px' }}>Pending Balance</p>
            <h4 className="mb-0 font-weight-bold text-danger">{format_rupees(total.balance)}</h4>
          </div>
        </Col>
      </Row>

      {/* Filters Section */}
      <div className="billing-form-card custom-card-boxshadow mb-4 py-4 px-4 bg-white">
        <Row className="align-items-end">
          {isAdmin && (
            <Col xs={12} md={3}>
              <FormField label="Filter by User" containerClassName="mb-3 mb-md-0">
                <Form.Control
                  as="select"
                  className="bg-light border-0"
                  value={selectedUser}
                  onChange={e => setSelectedUser(e.target.value)}
                >
                  <option value="">All Users</option>
                  {adminUsers && adminUsers.filter(u => u.role !== 'admin').map((u) => (
                    <option key={u.id} value={u.user_id}>{u.user_name}</option>
                  ))}
                </Form.Control>
              </FormField>
            </Col>
          )}
          <Col xs={12} md={isAdmin ? 3 : 4}>
            <FormField label="Date Range" containerClassName="mb-3 mb-md-0">
              <DatePicker
                ref={datePickerRef}
                placeholderText="Select Date Range"
                selected={startDate}
                onChange={onDateChange}
                startDate={startDate}
                endDate={endDate}
                selectsRange
                dateFormat="dd/MM/yyyy"
                maxDate={new Date()}
                portalId="root-portal"
                popperPlacement="bottom-start"
                popperModifiers={[{ name: "preventOverflow", options: { boundary: "viewport" } }]}
                withPortal={isMobile}
                customInput={
                  <NoKeyboardInput className="form-control bg-light border-0" style={{ cursor: 'pointer' }} />
                }
              />
            </FormField>
          </Col>
          <Col xs={12} md={isAdmin ? 3 : 5}>
            <FormField label="Patient Name Search" containerClassName="mb-3 mb-md-0">
              <Form.Control
                type="text"
                className="bg-light border-0"
                placeholder="Search by Patient..."
                value={patientName}
                onChange={e => setPatientNme(e.target.value)}
              />
            </FormField>
          </Col>
          <Col xs={12} md={3} className="d-grid">
            <Button onClick={handleSearch} variant="primary" className="w-100" style={{ fontWeight: 600, height: '40px', padding: '0 24px', borderRadius: '8px' }}>
              <i className="fa fa-search mr-2"></i>Search
            </Button>
          </Col>
        </Row>
      </div>

      <div className="billing-form-card custom-card-boxshadow p-0 overflow-hidden">
        {!loading ? (
          <ToolkitProvider
            keyField="id"
            data={invoices || []}
            columns={columnsToUse}
            search
          >
            {props => (
              <div className="premium-table-container" style={{ position: 'relative' }}>
                {downloadingId && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 10,
                    borderRadius: '8px'
                  }}>
                    <div className="spinner-border text-primary" role="status"></div>
                    <p className="mt-3 text-muted font-weight-bold" style={{ fontSize: '14px' }}>Downloading TRF - {downloadingId}</p>
                  </div>
                )}
                <div className="premium-table-header d-flex justify-content-between align-items-center">
                  <div className="search-bar-container" style={{ width: '300px' }}>
                    <SearchBar {...props.searchProps} placeholder="Quick search results..." />
                  </div>
                </div>
                <BootstrapTable
                  {...props.baseProps}
                  bordered={false}
                  hover
                  rowStyle={{ cursor: 'pointer' }}
                  rowEvents={{
                    onClick: (e, row) => {
                      setSelectedRow(row);
                      setShowActionModal(true);
                    }
                  }}
                  noDataIndication={() => (
                    <div className="p-5 text-center text-muted italic">
                      <i className="fa fa-info-circle mr-2"></i> No records found for this period.
                    </div>
                  )}
                  pagination={paginationFactory({
                    showTotal: true,
                    sizePerPage: 10,
                    hideSizePerPage: true,
                    className: "premium-pagination"
                  })}
                  wrapperClasses="table-responsive"
                  tableStyle={{ tableLayout: 'auto', whiteSpace: 'nowrap' }}
                />
              </div>
            )}
          </ToolkitProvider>
        ) : (
          <div className="p-5 text-center">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="mt-3 text-muted">Generating reports...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
